import { createFileRoute } from '@tanstack/react-router'
import { Plus, Pencil, Check, X, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { useConfirmDialog } from '@/hooks'
import {
    useAllCurrencies,
    useCreateCurrency,
    useUpdateCurrency,
    useToggleCurrency,
    useDeleteCurrency,
    useInitCurrencies,
    type Currency,
    type CreateCurrencyInput,
    type UpdateCurrencyInput,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/finance/currencies')({
    component: CurrenciesPage,
})

function CurrenciesPage() {
    const [showForm, setShowForm] = useState(false)
    const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null)

    const { data: currencies, isLoading, error } = useAllCurrencies()
    const createMutation = useCreateCurrency()
    const updateMutation = useUpdateCurrency()
    const toggleMutation = useToggleCurrency()
    const deleteMutation = useDeleteCurrency()
    const initMutation = useInitCurrencies()
    const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

    const handleCreate = (data: CreateCurrencyInput) => {
        createMutation.mutate(data, {
            onSuccess: () => setShowForm(false),
        })
    }

    const handleUpdate = (code: string, data: UpdateCurrencyInput) => {
        updateMutation.mutate(
            { code, data },
            {
                onSuccess: () => setEditingCurrency(null),
            }
        )
    }

    const handleDelete = async (code: string) => {
        const confirmed = await confirmWithPreset(
            'delete',
            `确定要删除货币 ${code} 吗？`
        )
        if (confirmed) {
            setLoading(true)
            try {
                await deleteMutation.mutateAsync(code)
            } finally {
                setLoading(false)
            }
        }
    }

    const handleInit = () => {
        initMutation.mutate()
    }

    if (error) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="text-destructive">加载失败: {error.message}</div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">货币管理</h1>
                    <p className="text-muted-foreground">
                        管理系统支持的货币列表（汇率在换汇时独立指定）
                    </p>
                </div>
                <div className="flex gap-2">
                    {currencies?.length === 0 && (
                        <button
                            onClick={handleInit}
                            disabled={initMutation.isPending}
                            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
                        >
                            {initMutation.isPending ? '初始化中...' : '初始化默认货币'}
                        </button>
                    )}
                    <button
                        onClick={() => setShowForm(true)}
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                    >
                        <Plus className="h-4 w-4" />
                        新增货币
                    </button>
                </div>
            </div>

            {/* 新增/编辑表单 */}
            {(showForm || editingCurrency) && (
                <CurrencyForm
                    currency={editingCurrency}
                    onSubmit={(data) => {
                        if (editingCurrency) {
                            handleUpdate(editingCurrency.code, data)
                        } else {
                            handleCreate(data as CreateCurrencyInput)
                        }
                    }}
                    onCancel={() => {
                        setShowForm(false)
                        setEditingCurrency(null)
                    }}
                    isPending={createMutation.isPending || updateMutation.isPending}
                />
            )}

            {/* 货币列表 */}
            {isLoading ? (
                <div className="flex h-64 items-center justify-center">
                    <div className="text-muted-foreground">加载中...</div>
                </div>
            ) : !currencies || currencies.length === 0 ? (
                <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
                    <div className="text-center">
                        <p className="text-lg font-medium">暂无货币</p>
                        <p className="text-sm text-muted-foreground">
                            点击上方按钮初始化默认货币或新增货币
                        </p>
                    </div>
                </div>
            ) : (
                <div className="rounded-lg border">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-muted/50">
                                <th className="px-4 py-3 text-left text-sm font-medium">代码</th>
                                <th className="px-4 py-3 text-left text-sm font-medium">名称</th>
                                <th className="px-4 py-3 text-left text-sm font-medium">符号</th>
                                <th className="px-4 py-3 text-center text-sm font-medium">小数位</th>
                                <th className="px-4 py-3 text-center text-sm font-medium">排序</th>
                                <th className="px-4 py-3 text-center text-sm font-medium">状态</th>
                                <th className="px-4 py-3 text-right text-sm font-medium">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currencies.map((currency) => (
                                <tr
                                    key={currency.code}
                                    className={cn(
                                        'border-b last:border-0 hover:bg-muted/30',
                                        !currency.isActive && 'opacity-50'
                                    )}
                                >
                                    <td className="px-4 py-3 font-mono font-medium">{currency.code}</td>
                                    <td className="px-4 py-3">{currency.name}</td>
                                    <td className="px-4 py-3 text-lg">{currency.symbol}</td>
                                    <td className="px-4 py-3 text-center">{currency.decimals}</td>
                                    <td className="px-4 py-3 text-center">{currency.sortOrder}</td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => toggleMutation.mutate(currency.code)}
                                            disabled={toggleMutation.isPending}
                                            className={cn(
                                                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                                                currency.isActive
                                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            )}
                                        >
                                            {currency.isActive ? (
                                                <>
                                                    <Check className="h-3 w-3" /> 启用
                                                </>
                                            ) : (
                                                <>
                                                    <X className="h-3 w-3" /> 禁用
                                                </>
                                            )}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            <button
                                                onClick={() => setEditingCurrency(currency)}
                                                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                                title="编辑"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => void handleDelete(currency.code)}
                                                disabled={deleteMutation.isPending}
                                                className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                title="删除"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmDialog />
        </div>
    )
}

// ==================== 表单组件 ====================

interface CurrencyFormProps {
    currency?: Currency | null
    onSubmit: (data: CreateCurrencyInput | UpdateCurrencyInput) => void
    onCancel: () => void
    isPending: boolean
}

function CurrencyForm({ currency, onSubmit, onCancel, isPending }: CurrencyFormProps) {
    const isEditing = !!currency
    const [formData, setFormData] = useState({
        code: currency?.code ?? '',
        name: currency?.name ?? '',
        symbol: currency?.symbol ?? '',
        decimals: currency?.decimals ?? 2,
        sortOrder: currency?.sortOrder ?? 0,
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (isEditing) {
            onSubmit({
                name: formData.name,
                symbol: formData.symbol,
                decimals: formData.decimals,
                sortOrder: formData.sortOrder,
            })
        } else {
            onSubmit(formData as CreateCurrencyInput)
        }
    }

    return (
        <div className="rounded-lg border p-4">
            <h3 className="mb-4 font-semibold">{isEditing ? '编辑货币' : '新增货币'}</h3>
            <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-5">
                <div>
                    <label className="mb-1 block text-sm font-medium">货币代码</label>
                    <input
                        type="text"
                        value={formData.code}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                        }
                        disabled={isEditing}
                        maxLength={10}
                        className="w-full rounded-md border px-3 py-2 text-sm font-mono uppercase focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-muted"
                        placeholder="USD"
                        required
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-medium">货币名称</label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="美元"
                        required
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-medium">货币符号</label>
                    <input
                        type="text"
                        value={formData.symbol}
                        onChange={(e) => setFormData((prev) => ({ ...prev, symbol: e.target.value }))}
                        maxLength={5}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="$"
                        required
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-medium">小数位数</label>
                    <input
                        type="number"
                        min={0}
                        max={4}
                        value={formData.decimals}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                decimals: parseInt(e.target.value) || 0,
                            }))
                        }
                        className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-sm font-medium">排序</label>
                    <input
                        type="number"
                        min={0}
                        value={formData.sortOrder}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                sortOrder: parseInt(e.target.value) || 0,
                            }))
                        }
                        className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
                <div className="flex items-end gap-2 md:col-span-5">
                    <button
                        type="submit"
                        disabled={isPending}
                        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isPending ? '保存中...' : '保存'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
                    >
                        取消
                    </button>
                </div>
            </form>
        </div>
    )
}
